import { useId, type KeyboardEvent, type ReactNode } from "react";

type Special = { value: string; label: string };

/** − value + with a big display number. An optional special first value
 * ("Auto", "Everyone") sits before the numeric range. The value itself is an
 * ARIA spinbutton, so arrow/Home/End keys work as well as the two buttons. */
export function Stepper({
  label,
  hint,
  value,
  min,
  max,
  step = 1,
  special,
  format,
  unit,
  onChange,
  disabled = false,
}: {
  label: ReactNode;
  hint?: ReactNode;
  value: number | string | undefined;
  min: number;
  max: number;
  step?: number;
  special?: Special;
  format?: (n: number) => string;
  unit?: string;
  onChange: (value: number | string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const values: (number | string)[] = [];
  if (special) values.push(special.value);
  for (let n = min; n <= max; n += step) values.push(n);
  let index = values.findIndex((v) => String(v) === String(value));
  if (index < 0) {
    // Snap a legacy or out-of-range value to the nearest allowed step.
    const n = Number(value);
    index = Number.isFinite(n)
      ? values.reduce<number>(
          (best, v, i) =>
            typeof v === "number" &&
            (best < 0 ||
              Math.abs(v - n) < Math.abs(Number(values[best]) - n))
              ? i
              : best,
          -1,
        )
      : 0;
    if (index < 0) index = 0;
  }
  const current = values[index];
  const text =
    typeof current === "number"
      ? format
        ? format(current)
        : String(current)
      : (special?.label ?? String(current));
  const go = (i: number) => {
    const next = values[Math.max(0, Math.min(values.length - 1, i))];
    if (String(next) !== String(value)) onChange(next);
  };
  const keys = (e: KeyboardEvent) => {
    const map: Record<string, number> = {
      ArrowUp: index + 1,
      ArrowRight: index + 1,
      ArrowDown: index - 1,
      ArrowLeft: index - 1,
      PageUp: index + 3,
      PageDown: index - 3,
      Home: 0,
      End: values.length - 1,
    };
    if (e.key in map && !disabled) {
      e.preventDefault();
      go(map[e.key]);
    }
  };
  const plainLabel = typeof label === "string" ? label : "value";
  return (
    <div className={`ui-field ui-stepper${disabled ? " is-disabled" : ""}`}>
      <div className="stepper-row">
        <span className="stepper-text">
          <span className="ui-label" id={`${id}-label`}>
            {label}
          </span>
          {hint ? (
            <span className="ui-hint" id={`${id}-hint`}>
              {hint}
            </span>
          ) : null}
        </span>
        <span className="stepper-control">
          <button
            type="button"
            className="stepper-button"
            aria-label={`Decrease ${plainLabel}`}
            disabled={disabled || index <= 0}
            onClick={() => go(index - 1)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
          <span
            className={`stepper-value${typeof current === "string" ? " is-special" : ""}`}
            role="spinbutton"
            tabIndex={disabled ? -1 : 0}
            aria-labelledby={`${id}-label`}
            aria-describedby={hint ? `${id}-hint` : undefined}
            aria-valuemin={special ? undefined : min}
            aria-valuemax={special ? undefined : max}
            aria-valuenow={typeof current === "number" ? current : undefined}
            aria-valuetext={unit && typeof current === "number" ? `${text} ${unit}` : text}
            aria-disabled={disabled || undefined}
            onKeyDown={keys}
          >
            {text}
            {unit && typeof current === "number" ? (
              <span className="stepper-unit" aria-hidden="true">
                {unit}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            className="stepper-button"
            aria-label={`Increase ${plainLabel}`}
            disabled={disabled || index >= values.length - 1}
            onClick={() => go(index + 1)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12h14M12 5v14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </span>
      </div>
    </div>
  );
}
